<template>
  <div class="ai-training-panel">
    <div class="ai-training-actions level is-mobile">
      <div class="level-right">
        <button class="button is-small is-light" data-testid="ai-training-add-ontology" :title="$t('add-terms')" @click="openAddOntologyModal">
          <span class="icon is-small"><i class="fas fa-plus"></i></span>
          <span>{{ $t('add-terms') }}</span>
        </button>
        <button class="button is-small is-light" data-testid="ai-training-refresh" :disabled="refreshing" :title="$t('refresh')" @click="refresh">
          <span class="icon is-small"><i class="fas fa-sync-alt" :class="{ 'fa-spin': refreshing }"></i></span>
        </button>
      </div>
    </div>
    <b-message v-if="refreshError" type="is-danger" has-icon>
      {{ $t('failed-to-refresh') }}
      <button class="button is-small" @click="refresh">{{ $t('retry') }}</button>
    </b-message>
    <ai-training-terms-panel
      :index="index"
      :project="project"
      :refresh-token="refreshToken"
      :selected-report-term-ids="selectedReportTermIds"
      @toggle-report-term="toggleReportTerm"
      @select="selectAnnotation"
      @centerView="centerView"
      @delete="handleDeletion"
      @updateTermsOrTracks="updateTermsOrTracks"
    />
    <section class="ai-training-report-area">
      <div class="ai-training-report-builder">
        <div class="field">
          <label class="label">{{ $t('report-type') }}</label>
          <div class="select is-small is-fullwidth">
            <select v-model="selectedReportType" data-testid="ai-training-report-type">
              <option value="malaria">{{ $t('malaria-report') }}</option>
            </select>
          </div>
        </div>
        <button
          class="button is-primary is-small is-fullwidth"
          data-testid="ai-training-generate-report"
          :class="{'is-loading': generatingReport}"
          :disabled="generatingReport"
          @click="generateReport"
        >
          {{ $t('generate-report') }}
        </button>
      </div>
      <div v-if="trainingMalariaResult" class="ai-training-report-result">
        <malaria-result-panel
          :result="trainingMalariaResult"
          :project="project"
          :index="index"
          :all-terms="allTerms"
          :ontology-terms="ontologyTerms"
          :images="images"
          :active-slice-id="activeSliceId"
          @overlayChange="handleTrainingOverlayChange"
          @settingsChange="handleTrainingSettingsChange"
          @select="selectAnnotation"
          @centerView="centerView"
          @deletion="handleDeletion"
          @updateTermsOrTracks="updateTermsOrTracks"
        />
      </div>
    </section>
  </div>
</template>

<script>
import { Annotation } from '@/api';
import { Action, listAnnotationsInGroup, updateAnnotationLinkProperties, updateTermProperties, updateTrackProperties } from '@/utils/annotation-utils';
import AiTrainingTermsPanel from './AITrainingTermsPanel';
import MalariaResultPanel from './MalariaResultPanel';
import viewerImageContext from './mixins/viewer-image-context';
import {getAllTerms} from '@/utils/ontology-utils';
import {fetchAllTrainingAnnotationsForTerm} from './ai-training-report-data';
import {createTrainingMalariaResult, validateTrainingMalariaTerms} from './ai-models/training-malaria';

export default {
  name: 'AiTrainingPanel',
  mixins: [viewerImageContext],
  components: {AiTrainingTermsPanel, MalariaResultPanel},
  props: {
    index: String,
    project: Object
  },
  data() {
    return {
      refreshToken: 0,
      refreshing: false,
      refreshError: false,
      selectedReportType: 'malaria',
      selectedReportTermIds: [],
      generatingReport: false,
      reportRequestId: 0,
      trainingMalariaResult: null
    };
  },
  computed: {
    imageModule() {
      return this.$store.getters['currentProject/imageModulePath'](this.index);
    },
    imageGroupId() {
      return this.$store.getters[this.imageModule + 'imageGroupId'];
    },
    imageId() {
      return this.image && this.image.id;
    },
    allTerms() {
      return this.$store.getters[`${this.imageModule}terms`] ||
        (this.imageWrapper?.ontologies || []).flatMap(ontology => getAllTerms(ontology));
    },
    ontologyTerms() {
      return this.imageWrapper?.style?.ontologyTerms || {};
    },
    images() {
      return this.image ? [this.image] : [];
    },
    activeSliceId() {
      const slices = Array.isArray(this.activeSlices) ? this.activeSlices : Object.values(this.activeSlices || {});
      return slices[0]?.id;
    }
  },
  watch: {
    imageId(value, oldValue) {
      if (value !== oldValue) {
        this.reportRequestId++;
        this.selectedReportTermIds = [];
        this.trainingMalariaResult = null;
        this.generatingReport = false;
      }
    }
  },
  methods: {
    openAddOntologyModal() {
      this.$eventBus.$emit('openAddOntologyModal');
    },
    termById(id) {
      return this.allTerms.find(term => String(term.id) === String(id));
    },
    toggleReportTerm(term) {
      const id = String(term.id);
      this.selectedReportTermIds = this.selectedReportTermIds.includes(id)
        ? this.selectedReportTermIds.filter(selectedId => selectedId !== id)
        : [...this.selectedReportTermIds, id];
    },
    async generateReport() {
      const selectedTerms = this.selectedReportTermIds.map(id => this.termById(id)).filter(Boolean);
      const parasiteTerm = selectedTerms.find(term => term.name === 'Malaria_Detected');
      const wbcTerm = selectedTerms.find(term => term.name === 'White_Blood_Cell');
      const validation = validateTrainingMalariaTerms(parasiteTerm, wbcTerm);
      if (!validation.valid || selectedTerms.length !== 2) {
        const error = validation.valid ? 'invalid-report-terms' : validation.error;
        this.$notify({type: 'error', text: this.$t(`ai-training-${error}`)});
        return;
      }
      const requestId = ++this.reportRequestId;
      const imageId = this.imageId;
      const isCurrent = () => requestId === this.reportRequestId && imageId === this.imageId;
      this.generatingReport = true;
      try {
        const [parasiteAnnotations, wbcAnnotations] = await Promise.all([
          fetchAllTrainingAnnotationsForTerm({projectId: this.project.id, imageId, termId: parasiteTerm.id, isCurrent}),
          fetchAllTrainingAnnotationsForTerm({projectId: this.project.id, imageId, termId: wbcTerm.id, isCurrent})
        ]);
        if (!isCurrent()) {
          return;
        }
        this.trainingMalariaResult = createTrainingMalariaResult({
          parasiteTerm,
          wbcTerm,
          parasiteAnnotations,
          wbcAnnotations
        });
        this.handleTrainingOverlayChange(this.trainingMalariaResult.overlay);
      } catch (error) {
        if (error.code !== 'STALE_AI_TRAINING_REPORT' && isCurrent()) {
          console.error('Failed to generate AI training malaria report:', error);
          this.$notify({type: 'error', text: this.$t('ai-training-report-generation-failed')});
        }
      } finally {
        if (isCurrent()) {
          this.generatingReport = false;
        }
      }
    },
    handleTrainingOverlayChange(overlay) {
      this.$store.commit(`${this.imageModule}setAITrainingMalariaOverlay`, overlay);
    },
    handleTrainingSettingsChange(settings) {
      this.$store.commit(`${this.imageModule}setAITrainingMalariaSettings`, settings);
    },
    async refresh() {
      this.refreshing = true;
      this.refreshError = false;
      try {
        await this.$store.dispatch(`${this.imageModule}refreshOntologiesFromImage`);
        this.refreshToken++;
      } catch (error) {
        console.error('Failed to refresh AI training ontologies:', error);
        this.refreshError = true;
      } finally {
        this.refreshing = false;
      }
    },
    selectAnnotation({annot, options = {}}) {
      this.$eventBus.$emit('selectAnnotation', {
        index: options.trySameView ? this.index : null,
        annot,
        center: true
      });
    },
    centerView({annot, sameView = false}) {
      this.$eventBus.$emit('selectAnnotation', {
        index: sameView ? this.index : null,
        annot,
        center: true
      });
    },
    async updateTermsOrTracks(annot) {
      try {
        const updated = await annot.clone().fetch();
        updated.imageGroup = this.imageGroupId;
        await updateTermProperties(updated);
        await updateTrackProperties(updated);
        await updateAnnotationLinkProperties(updated);
        this.$eventBus.$emit('editAnnotation', updated);
        this.$store.commit(`${this.imageModule}changeAnnotSelectedFeature`, {
          indexFeature: 0,
          annot: updated
        });
      } catch (error) {
        console.error('Failed to refresh an AI training annotation:', error);
        this.$notify({type: 'error', text: this.$t('notif-error-annotation-update')});
      }
    },
    async handleDeletion(annot) {
      try {
        this.$store.commit(`${this.imageModule}addAction`, {annot, type: Action.DELETE});
        if (annot.group) {
          const otherLink = (annot.annotationLink || []).find(link => link.annotation !== annot.id);
          const edited = annot.annotationLink && annot.annotationLink.length === 2 && otherLink
            ? [await Annotation.fetch(otherLink.annotation)]
            : await listAnnotationsInGroup(annot.project, annot.group);
          edited.forEach(item => this.$eventBus.$emit('editAnnotation', item));
        }
        this.$eventBus.$emit('deleteAnnotation', annot);
      } catch (error) {
        console.error('Failed to delete an AI training annotation:', error);
        this.$notify({type: 'error', text: this.$t('notif-error-annotation-deletion')});
      }
    }
  },
  mounted() {
    this.refresh();
  },
  beforeDestroy() {
    this.reportRequestId++;
    if (this.trainingMalariaResult) {
      this.$store.commit(`${this.imageModule}clearAITrainingMalariaOverlay`);
    }
  }
};
</script>

<style scoped lang="scss">
.ai-training-panel { color: inherit; }
.ai-training-actions { margin-bottom: .7rem; }
.ai-training-actions .level-right { gap: .35rem; }
.ai-training-report-builder {
  padding: .75rem;
  margin-bottom: .85rem;
  border: 1px solid rgba(190, 214, 238, .12);
  border-radius: 8px;
  background: rgba(222, 238, 255, .04);
}
.ai-training-report-area { margin-top: .9rem; }
.ai-training-report-builder .label { color: inherit; font-size: .75rem; }
.ai-training-report-result { margin-top: .9rem; }
</style>
